<template>
  <v-container fluid class="pnl-report-page">
    <header class="pnl-report-header">
      <h1 class="pnl-report-title">{{ $t('nav.pnlReport') }}</h1>
      <div class="pnl-report-controls">
        <v-btn-toggle
          v-model="selectedPeriod"
          mandatory
          color="primary"
          density="compact"
          variant="outlined"
          divided
          class="period-toggle"
        >
          <v-btn value="day" size="small">{{ $t('budgetReport.periodDay') }}</v-btn>
          <v-btn value="month" size="small">{{ $t('budgetReport.periodMonth') }}</v-btn>
          <v-btn value="year" size="small">{{ $t('budgetReport.periodYear') }}</v-btn>
        </v-btn-toggle>

        <v-text-field
          v-if="selectedPeriod === 'day'"
          v-model="dateTo"
          :label="$t('budgetReport.dateTo')"
          type="date"
          density="compact"
          hide-details
          variant="outlined"
          class="control-field"
          @change="loadReport"
        />
        <v-text-field
          v-else-if="selectedPeriod === 'month'"
          v-model="selectedMonth"
          type="month"
          density="compact"
          hide-details
          variant="outlined"
          class="control-field"
          @change="loadReport"
        />
        <v-text-field
          v-else
          v-model.number="selectedYear"
          type="number"
          density="compact"
          hide-details
          variant="outlined"
          class="control-field control-field-year"
          @change="loadReport"
        />
      </div>
    </header>

    <div class="pnl-report-table-wrapper">
      <table class="pnl-report-table">
        <thead>
          <tr>
            <th class="sticky-col col-name">{{ $t('financialReports.reportData') }}</th>
            <th v-for="col in columns" :key="col.date" class="col-value">
              <span v-if="selectedPeriod !== 'day'" class="col-label">{{ col.label }}</span>
              <span class="col-date">{{ formatColDate(col.date) }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr class="group-row">
            <td class="sticky-col col-name">{{ $t('financialReports.profitSection') }}</td>
            <td v-for="col in columns" :key="`gp-${col.date}`" class="col-value text-success">
              {{ formatNumber(totals.profit_total?.[col.date] || 0) }}
            </td>
          </tr>
          <tr v-for="row in profitRows" :key="row.key">
            <td class="sticky-col col-name">{{ getRowLabel(row) }}</td>
            <td v-for="col in columns" :key="`${row.key}-${col.date}`" class="col-value">
              {{ formatNumber(data[row.key]?.[col.date] || 0) }}
            </td>
          </tr>

          <tr class="group-row">
            <td class="sticky-col col-name">{{ $t('financialReports.lossSection') }}</td>
            <td v-for="col in columns" :key="`gl-${col.date}`" class="col-value text-error">
              {{ formatNumber(totals.expenses_total?.[col.date] || 0) }}
            </td>
          </tr>
          <tr v-for="row in lossRows" :key="row.key">
            <td class="sticky-col col-name">{{ getRowLabel(row) }}</td>
            <td v-for="col in columns" :key="`${row.key}-${col.date}`" class="col-value">
              {{ formatNumber(data[row.key]?.[col.date] || 0) }}
            </td>
          </tr>
          <tr class="totals-row">
            <td class="sticky-col col-name">{{ $t('financialReports.netPL') }}</td>
            <td v-for="col in columns" :key="`np-${col.date}`" class="col-value">
              <span :class="(totals.net_pl?.[col.date] || 0) >= 0 ? 'text-success' : 'text-error'">
                {{ formatNumber(totals.net_pl?.[col.date] || 0) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <v-overlay v-model="loading" contained class="align-center justify-center" scrim="rgba(255,255,255,0.7)">
      <v-progress-circular indeterminate size="48" width="3" />
    </v-overlay>
  </v-container>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { apiGet } from '../utils/api'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const now = new Date()
const today = now.toISOString().slice(0, 10)
const currentYear = now.getFullYear()

const selectedPeriod = ref<string>('day')
const dateFrom = ref(today)
const dateTo = ref(today)
const selectedMonth = ref(today.slice(0, 7))
const selectedYear = ref(currentYear)
const loading = ref(false)

const columns = ref<Array<{ label: string; date: string }>>([])
const rows = ref<Array<{ key: string; name: string }>>([])
const data = ref<Record<string, Record<string, number>>>({})
const totals = ref<Record<string, Record<string, number>>>({})

const profitRows = computed(() => rows.value.filter((row: any) => row.section === 'profit'))
const lossRows = computed(() => rows.value.filter((row: any) => row.section === 'expenses'))

function initFromQuery() {
  const q = route.query
  selectedPeriod.value = (q.period as string) || 'day'
  dateTo.value = (q.date_to as string) || (q.date as string) || today
  dateFrom.value = (q.date_from as string) || ''
  selectedMonth.value = (q.month as string) || today.slice(0, 7)
  const y = parseInt(q.year as string, 10)
  selectedYear.value = Number.isFinite(y) ? y : currentYear
}

function updateUrlFromParams() {
  const query: Record<string, string> = { period: selectedPeriod.value }
  if (selectedPeriod.value === 'day') {
    query.date_to = dateTo.value
  } else if (selectedPeriod.value === 'month') {
    query.month = selectedMonth.value
  } else {
    query.year = String(selectedYear.value)
  }
  router.replace({ path: route.path, query })
}

const formatNumber = (value: number) =>
  Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const formatColDate = (dateStr: string) => {
  const d = new Date(`${dateStr}T00:00:00`)
  return d.toLocaleDateString('default', { month: 'short', day: 'numeric' })
}

const getRowLabel = (row: any) => {
  if (row.key === 'profit_exchange_differences' || row.key === 'expense_exchange_differences') {
    return t('financialReports.exchangeDifferences')
  }
  if (row.key === 'profit_staking') {
    return t('financialReports.staking')
  }
  if (row.key === 'expense_commissions') {
    return t('financialReports.commissions')
  }
  return row.name
}

const loadReport = async () => {
  updateUrlFromParams()
  loading.value = true
  try {
    const params = new URLSearchParams({ period: selectedPeriod.value })
    if (selectedPeriod.value === 'day') {
      params.set('date_to', dateTo.value)
      if (dateFrom.value) params.set('date_from', dateFrom.value)
    } else if (selectedPeriod.value === 'month') {
      params.set('date', `${selectedMonth.value}-01`)
    } else {
      params.set('date', `${selectedYear.value}-01-01`)
    }

    const response = await apiGet(`/api/pnl-report?${params}`)
    if (response.ok) {
      const result = await response.json()
      columns.value = result.columns || []
      rows.value = result.rows || []
      data.value = result.data || {}
      totals.value = result.totals || {}
    }
  } catch (error) {
    console.error('Load P&L report error:', error)
  } finally {
    loading.value = false
  }
}

watch(selectedPeriod, () => loadReport())

onMounted(() => {
  initFromQuery()
  loadReport()
})

watch(() => route.query, () => {
  initFromQuery()
  loadReport()
}, { deep: true })
</script>

<style scoped>
.pnl-report-page { max-width: 1600px; margin: 0 auto; }
.pnl-report-header { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 1rem 1.5rem; margin-bottom: 1.25rem; padding-bottom: 1rem; border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity)); }
.pnl-report-title { font-size: 1.5rem; font-weight: 500; margin: 0; color: rgba(var(--v-theme-on-surface), 0.9); }
.pnl-report-controls { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 0.75rem 1.25rem; }
.period-toggle { border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity)); border-radius: 6px; overflow: hidden; background: rgb(var(--v-theme-surface)); }
.control-field { max-width: 11rem; }
.control-field-year { max-width: 6rem; }
.pnl-report-table-wrapper { overflow-x: auto; border-radius: 8px; border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity)); background: rgb(var(--v-theme-surface)); }
.pnl-report-table { width: 100%; border-collapse: collapse; min-width: 700px; }
.pnl-report-table th, .pnl-report-table td { padding: 0.625rem 0.875rem; border-bottom: 1px solid rgba(var(--v-border-color), 0.4); }
.pnl-report-table thead th { background: rgba(var(--v-theme-on-surface), 0.04); position: sticky; top: 0; z-index: 2; }
.sticky-col { position: sticky; left: 0; z-index: 3; background: rgb(var(--v-theme-surface)); border-right: 1px solid rgba(var(--v-border-color), 0.35); min-width: 220px; }
.col-name { text-align: left; }
.col-value { text-align: right; min-width: 120px; font-variant-numeric: tabular-nums; }
.col-label { display: block; }
.col-date { display: block; font-size: 0.6875rem; opacity: 0.7; margin-top: 0.125rem; }
.totals-row td { font-weight: 700; }
.group-row td {
  font-weight: 700;
  background: rgba(var(--v-theme-on-surface), 0.06);
}
</style>
